import { ChangeEvent, ReactElement, ReactNode } from "react";
import { ControllerProps, useForm } from "react-hook-form";

import { Button } from "@/components/_atoms/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/_atoms/form";
import { Input } from "@/components/_atoms/input";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useMunicipalPropertyFormSchema } from "@/domain/municipal-property/hooks/use-municipal-property-form-schema";
import { buildEmptyMunicipalPropertyCreateForm } from "@/domain/municipal-property/models/empty-municipal-property-create-form";
import { MunicipalPropertyCreateFormValues } from "@/domain/municipal-property/models/municipal-property-create-form-values";
import { WorkspaceMunicipalityOption } from "@/domain/municipal-property/models/workspace-municipality-option.model";
import { MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS } from "@ark/shared";
import { zodResolver } from "@hookform/resolvers/zod";

import { MunicipalPropertyMasterDataFields } from "./municipal-property-master-data-fields";
import { MunicipalPropertyMunicipalityFieldset } from "./municipal-property-municipality-fieldset";

interface MunicipalPropertyCreateFormProps {
  municipalityList: readonly WorkspaceMunicipalityOption[];
  defaultValues?: MunicipalPropertyCreateFormValues;
  maxEffectiveYear?: number;
  submitLabel: string;
  cancelLabel: string;
  alert?: ReactNode;
  isFieldsDisabled?: boolean;
  isSubmitDisabled?: boolean;
  onSubmit: (values: MunicipalPropertyCreateFormValues) => Promise<void>;
  onCancel: () => void;
}

export function MunicipalPropertyCreateForm({
  municipalityList,
  defaultValues,
  maxEffectiveYear = MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.maxEffectiveYear,
  submitLabel,
  cancelLabel,
  alert,
  isFieldsDisabled = false,
  isSubmitDisabled = false,
  onSubmit,
  onCancel,
}: MunicipalPropertyCreateFormProps): ReactElement {
  const { t } = useDomainTranslation("municipal-property");
  const baseSchema = useMunicipalPropertyFormSchema();
  const schema = baseSchema.refine((values) => values.effectiveYear <= maxEffectiveYear, {
    path: ["effectiveYear"],
    message: t("register.property.effectiveYearTooLate"),
  });
  const form = useForm<MunicipalPropertyCreateFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues ?? buildEmptyMunicipalPropertyCreateForm(),
  });

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit((values) => onSubmit(values))}
        className="flex flex-col gap-5"
        data-testid="municipal-property-create-form"
      >
        {alert}
        <fieldset disabled={form.formState.isSubmitting || isFieldsDisabled} className="flex min-w-0 flex-col gap-5">
          <FormField
            control={form.control}
            name="effectiveYear"
            render={({
              field,
            }: Parameters<
              ControllerProps<MunicipalPropertyCreateFormValues, "effectiveYear">["render"]
            >[0]): ReactElement => (
              <FormItem>
                <FormLabel>{t("history.effectiveYear")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.minEffectiveYear}
                    max={maxEffectiveYear}
                    {...field}
                    onChange={(event: ChangeEvent<HTMLInputElement>): void =>
                      field.onChange(Number(event.target.value))
                    }
                  />
                </FormControl>
                <FormDescription>{t("history.yearHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <MunicipalPropertyMasterDataFields control={form.control} shouldFocusName />

          <MunicipalPropertyMunicipalityFieldset control={form.control} municipalityList={municipalityList} />
        </fieldset>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" size="xs" onClick={onCancel} disabled={form.formState.isSubmitting}>
            {cancelLabel}
          </Button>
          <Button type="submit" size="xs" disabled={form.formState.isSubmitting || isSubmitDisabled}>
            {form.formState.isSubmitting ? t("create.submitting") : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
